import { useUser } from "@features/user/use-user";
import { Button, Checkbox, Flex, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { ResponsiveModal } from "@components/responsive-modal";

export const useModelConsentModal = () => {
  const queryClient = useQueryClient();
  const modalState = useQuery<{
    open: boolean;
    modelId: string;
    modelName: string;
    onConsent: () => void;
  }>(
    ["model-consent-modal"],
    () => {
      return {
        open: false,
        modelId: "",
        modelName: "",
        onConsent: () => {},
      };
    },
    { enabled: false }
  );

  return {
    state: modalState.data,
    setOpen: (
      open: boolean,
      modelId = "",
      modelName = "",
      onConsent = () => {}
    ) => {
      queryClient.setQueryData(["model-consent-modal"], {
        open,
        modelId,
        modelName,
        onConsent,
      });
    },
  };
};

export const ModelConsentModal = () => {
  const { state, setOpen } = useModelConsentModal();
  const { t } = useTranslation();
  const { consent } = useUser();
  const [personalUseConsent, setPersonalUseConsent] = useState(false);
  const [legalUseConsent, setLegalUseConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!personalUseConsent || !legalUseConsent || !state?.modelId) return;

    setIsSubmitting(true);
    await consent("models", state.modelId);
    setIsSubmitting(false);
    state?.onConsent();
    handleClose();
  };

  if (!state) return null;

  const footer = (
    <Flex gap="3" justify="end">
      <Button variant="soft" onClick={handleClose}>
        {t("general.cancel")}
      </Button>
      <Button
        onClick={handleSubmit}
        disabled={!personalUseConsent || !legalUseConsent || isSubmitting}
      >
        {isSubmitting
          ? t("consent.model.submitting")
          : t("consent.model.submit")}
      </Button>
    </Flex>
  );

  return (
    <ResponsiveModal
      isOpen={state.open}
      onClose={handleClose}
      title={t("consent.model.title", { name: state.modelName })}
      height="auto"
      maxHeight="90vh"
      footer={footer}
    >
      <div>
        <Text size="2" mb="4">
          {t("consent.model.description")}
        </Text>

        <Flex direction="column" gap="3" className="mb-4">
          <Flex gap="2" align="center">
            <Checkbox
              id="personal-use"
              checked={personalUseConsent}
              onCheckedChange={(checked) =>
                setPersonalUseConsent(checked as boolean)
              }
            />
            <Text as="label" htmlFor="personal-use" size="2">
              {t("consent.model.personal_use")}
            </Text>
          </Flex>

          <Flex gap="2" align="center">
            <Checkbox
              id="legal-use"
              checked={legalUseConsent}
              onCheckedChange={(checked) =>
                setLegalUseConsent(checked as boolean)
              }
            />
            <Text as="label" htmlFor="legal-use" size="2">
              {t("consent.model.legal_use")}
            </Text>
          </Flex>
        </Flex>
      </div>
    </ResponsiveModal>
  );
};
